// ABOUTME: RTK Query endpoints for the news + meldingen content system on root-api.
// ABOUTME: Multipart save/update use queryFn to bypass rootSlice's hardcoded Content-Type: application/json header.

import { rootSlice } from "../createRootSlice";
import { config } from "../apiUrlConfig";

const buildArticleFormData = ({ payload, files }) => {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    if (files) {
        Object.entries(files).forEach(([key, file]) => {
            if (file) formData.append(key, file);
        });
    }
    return formData;
};

const multipartFetch = async (url, method, formData, api) => {
    const state = api.getState();
    const token = state.auth.token;
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    try {
        const response = await fetch(url, { method, headers, body: formData });
        if (!response.ok) {
            let errorData = null;
            try { errorData = await response.json(); } catch { /* noop */ }
            return { error: { status: response.status, data: errorData } };
        }
        return { data: await response.json() };
    } catch (error) {
        return { error: { status: "FETCH_ERROR", error: error.message } };
    }
};

export const newsRootSlice = rootSlice.injectEndpoints({
    endpoints: (builder) => ({
        getNewsArticles: builder.query({
            query: ({ articleType, status } = {}) => {
                const params = new URLSearchParams();
                if (articleType) params.set("articleType", articleType);
                if (status) params.set("status", status);
                const qs = params.toString();
                return {
                    url: `news${qs ? `?${qs}` : ""}`,
                    method: "GET",
                };
            },
            providesTags: () => [{ type: "Articles" }],
        }),

        getNewsArticle: builder.query({
            query: (id) => ({
                url: `news/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "ArticleSingle", id }],
        }),

        addNewsArticle: builder.mutation({
            queryFn: async (args, api) => {
                const formData = buildArticleFormData(args);
                return multipartFetch(`${config.url.API_ROOT_URL}news`, "POST", formData, api);
            },
            invalidatesTags: () => [{ type: "Articles" }],
        }),

        updateNewsArticle: builder.mutation({
            queryFn: async (args, api) => {
                const { id, ...rest } = args;
                const formData = buildArticleFormData(rest);
                return multipartFetch(`${config.url.API_ROOT_URL}news/${id}`, "PUT", formData, api);
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Articles" },
                { type: "ArticleSingle", id: arg.id },
            ],
        }),

        publishNewsArticle: builder.mutation({
            query: (id) => ({
                url: `news/${id}/publish`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Articles" },
                { type: "ArticleSingle", id },
                { type: "NewsFeed" },
                { type: "NewsArchive" },
                { type: "ActiveMeldingen" },
            ],
        }),

        unpublishNewsArticle: builder.mutation({
            query: (id) => ({
                url: `news/${id}/unpublish`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Articles" },
                { type: "ArticleSingle", id },
                { type: "NewsFeed" },
                { type: "NewsArchive" },
                { type: "ActiveMeldingen" },
            ],
        }),

        resolveNewsArticle: builder.mutation({
            query: (id) => ({
                url: `news/${id}/resolve`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Articles" },
                { type: "ArticleSingle", id },
                { type: "ActiveMeldingen" },
            ],
        }),

        getMeldingenList: builder.query({
            query: ({ args }) => ({
                url: `news/meldingen/list?page=${args.page}&size=${args.size}`,
                method: "POST",
                body: args.body || {},
            }),
            providesTags: () => [{ type: "Articles" }],
        }),

        deleteNewsArticle: builder.mutation({
            query: (id) => ({
                url: `news/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Articles" },
                { type: "ArticleSingle", id },
            ],
        }),

        getNewsFeed: builder.query({
            query: () => ({
                url: `news/feed`,
                method: "GET",
            }),
            providesTags: () => [{ type: "NewsFeed" }],
        }),

        getNewsArchive: builder.query({
            query: ({ page = 1, size = 20 } = {}) => {
                const params = new URLSearchParams();
                params.set("page", String(page));
                params.set("size", String(size));
                return {
                    url: `news/archive?${params.toString()}`,
                    method: "GET",
                };
            },
            providesTags: () => [{ type: "NewsArchive" }],
        }),

        getActiveMeldingen: builder.query({
            query: () => ({
                url: `news/meldingen/active`,
                method: "GET",
            }),
            providesTags: () => [{ type: "ActiveMeldingen" }],
        }),

        markArticleSeen: builder.mutation({
            query: (id) => ({
                url: `news/${id}/seen`,
                method: "POST",
            }),
            // NewsFeed is snapshotted by MNewsCarousel for the session; only meldingen need live invalidation.
            invalidatesTags: () => [{ type: "ActiveMeldingen" }],
        }),
    }),
});

export const {
    useGetNewsArticlesQuery,
    useGetNewsArticleQuery,
    useAddNewsArticleMutation,
    useUpdateNewsArticleMutation,
    usePublishNewsArticleMutation,
    useUnpublishNewsArticleMutation,
    useResolveNewsArticleMutation,
    useGetMeldingenListQuery,
    useDeleteNewsArticleMutation,
    useGetNewsFeedQuery,
    useGetNewsArchiveQuery,
    useGetActiveMeldingenQuery,
    useMarkArticleSeenMutation,
} = newsRootSlice;
